import { useMutation } from "@apollo/client";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import {
  ErrorCode,
  ExecuteLlmQuickWinResult,
} from "@towersystems/roam-common/lib/generated-types";
import { ScreenContext } from "./common";
import React from "react";
import {
  Button,
  Divider,
  FlatActionList,
  FlatActionListItem,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import { ProductTagsLlmQuickWinRootParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Colors, Spacings, View } from "react-native-ui-lib";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useTranslation } from "../../../../utilities/i18n";

export interface ProductTagsReviewScreenProps
  extends StackScreenProps<
    ProductTagsLlmQuickWinRootParamList,
    "ProductTagsReviewScreen"
  > {}

export const ProductTagsReviewScreen = ({
  navigation,
}: ProductTagsReviewScreenProps) => {
  const screenContext = React.useContext(ScreenContext);
  const { onSubmit, originalFormValues } = screenContext;

  const [generatedTags, setGeneratedTags] = React.useState<
    string[] | undefined
  >(undefined);

  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);

  const flashMessagesContext = useFlashMessages();
  const transitionEnded = useHasTransitionEnded(navigation);
  const { t } = useTranslation("product");

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  React.useEffect(() => {
    (async () => {
      return handleOnGenerate();
    })();
  }, []);

  const handleOnGenerate = async () => {
    setGeneratedTags(undefined);
    setSelectedTags([]);
    return m({
      variables: {
        input: {
          product_tags_quick_win: {
            productName: originalFormValues.name,
            productDescription: originalFormValues.description || "",
            existingTags: originalFormValues.tags?.join(", ") || undefined,
            categoryId: originalFormValues.categoryId || null,
            departmentId: originalFormValues.departmentId || null,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          const tagsString = result.result as string;
          const tagsArray = tagsString
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0);
          setGeneratedTags(tagsArray);
          setSelectedTags(tagsArray);
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
          flashMessagesContext.showMessage({
            message: "Failed to generate description. Check your credentials.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        const msg = error?.message ?? "Failed to generate description";

        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        if (
          [
            ErrorCode.INVALID_LLM_CREDENTIALS_ERROR,
            ErrorCode.MISSING_LLM_STRATEGY_ERROR,
          ].includes(msg)
        ) {
          navigation.goBack();
        }
      });
  };

  const handleOnSubmit = async () => {
    return m({
      variables: {
        input: {
          product_tags_quick_win: {
            productName: originalFormValues.name,
            productDescription: originalFormValues.description || "",
            existingTags: originalFormValues.tags?.join(", ") || undefined,
            categoryId: originalFormValues.categoryId || null,
            departmentId: originalFormValues.departmentId || null,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          const tagsString = result.result as string;
          const tagsArray = tagsString
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0);
          setGeneratedTags(tagsArray);
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
          flashMessagesContext.showMessage({
            message: "Failed to generate description. Check your credentials.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        const msg = error?.message ?? "Failed to generate description";

        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        if (
          [
            ErrorCode.INVALID_LLM_CREDENTIALS_ERROR,
            ErrorCode.MISSING_LLM_STRATEGY_ERROR,
          ].includes(msg)
        ) {
          navigation.goBack();
        }
      });
  };

  if (generatedTags === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const items: FlatActionListItem[] = generatedTags.map((tag, index) => ({
    key: `generated-tag-${index}`,
    title: tag,
    value: tag,
    selected: selectedTags.includes(tag),
    onPress: () => {
      if (selectedTags.includes(tag)) {
        setSelectedTags(selectedTags.filter((t) => t !== tag));
      } else {
        setSelectedTags([...selectedTags, tag]);
      }
    },
    contentStyles: {
      backgroundColor: selectedTags.includes(tag) ? "#ebebeb" : undefined,
      padding: 12,
      borderRadius: 8,
    },
    componentRight: () => {
      return selectedTags.includes(tag) ? (
        <Feather name="check" size={24} color={Colors.action} />
      ) : null;
    },
  }));

  const disabled = generatedTags === undefined || generatedTags.length === 0;

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={disabled}
          label={t("button.generate_tags")}
          isAction
          onPress={() => handleOnGenerate()}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
      <View flex>
        <Button
          disabled={generatedTags === undefined || selectedTags.length === 0}
          label={t("button.add_generated_tags")}
          backgroundColor={Colors.action}
          color="white"
          onPress={() => onSubmit(selectedTags || [])}
        ></Button>
      </View>
    </View>
  );

  return (
    <Page
      title={t("title.generated_ai_tags")}
      secondaryTitle={t("title.generated_ai_tags_secondary")}
      footer={footerMarkup}
    >
      <View>
        <FlatActionList
          items={items}
          itemSeparatorComponent={<Divider marginVertical={Spacings.s2} />}
        />
      </View>
    </Page>
  );
};
